import { EventAggregator } from 'aurelia-event-aggregator';
import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { TableHeader } from 'components/table/table-content';
import { PubSub } from 'lib/event/PubSub';
import { FilterManager } from 'lib/tables/FilterManager';
import { DetergentTypeService } from 'services/detergent-type-service';
import { ErpSystemService } from 'services/erp-system-service';
import { ErrorService } from 'services/error-service';
import { Filters, filterTypes } from 'elements/Filter';
import { ErpSystem } from 'models/ErpSystem';

type DetergentType = {
  Id: number;
  Name: string;
  ArticleNo: string;
  ErpSystemName: string;
};

@autoinject
export class DetergentTypeList {
  protected tableData: Array<DetergentType>;
  protected activeTableRow: number;

  protected erpSystems: ErpSystem[] = [];

  protected tableHeaders: TableHeader<DetergentType>[] = [
    { key: 'Name', label: 'general.name', sortable: true },
    { key: 'ArticleNo', label: 'general.articleno', sortable: true },
    { key: 'ErpSystemName', label: 'general.erpSystem', sortable: false },
  ];

  private constructor(
    private errorService: ErrorService,
    private eventAggregator: EventAggregator,
    private router: Router,
    private detergentTypeService: DetergentTypeService,
    protected erpSystemService: ErpSystemService,
    private t: I18N,
    protected fm: FilterManager,
    private pubsub: PubSub
  ) {
    pubsub.sub('filter-manager:filter-changed', () => {
      this.updateView();
    });
  }

  activate(params: { Id: number }) {
    if (params.Id) {
      this.activeTableRow = params.Id;
    } else {
      this.activeTableRow = 0;
    }
  }

  async attached() {
    this.eventAggregator.subscribe('detergentTypeListReset', () => {
      this.activeTableRow = null;
      return this.updateView();
    });

    await this.load();

    this.fm.init({
      context: 'detergent-type',
      storageKey: 'detergent-type',
      filters: {
        erpIds: {
          name: 'erpIds',
          label: this.t.tr('general.erpSystem'),
          type: filterTypes.CHECKBOX,
          options: this.erpSystems,
          query: 'erpIds',
        },
      } satisfies Filters,
      defaultFilters: {
        erpIds: true,
      },
    });

    await this.updateView();
  }

  private async load() {
    const erpSystems = await this.erpSystemService.getAll();
    const newList = erpSystems.map((e) => {
      const newItem = { ...e, Name: e.Title };
      delete newItem.Title;
      return newItem;
    });

    this.erpSystems = newList;
  }

  private async updateView() {
    try {
      const response = await this.detergentTypeService.getDetegentTypeList(this.fm.queryFilter);

      if (!response) return;
      this.fm.paginationTotalRecords = +response.headers.get('x-total-count') || 0;

      this.tableData = response.data;
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  protected setOrderByColumn(column: string) {
    this.fm.setOrderByColumn(column);
  }

  gotoDetailView(id: number) {
    this.activeTableRow = id;
    this.router.navigateToRoute('detergent-type-detail', { Id: id });
  }

  protected detached() {
    this.pubsub.unsub();
  }

  protected unbind() {
    this.pubsub.unsub();
  }
}
